import { useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { Icon } from '@/components/Icon';
import type { AdMedia, UpdateMediaPayload } from '@/api/ads';

/** 1:1 Upload::make(...) в старых Orchid-лейаутах: один файл, 2 МБ, картинка или видео. */
const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPT = 'image/*,video/*';

/**
 * Медиафайл объявления/баннера — ровно один. Файл уходит на сервер сразу при
 * выборе (как это делал Upload-виджет в Orchid), форма хранит только его id;
 * привязка к записи происходит при сохранении.
 *
 * Параметры файла (отображаемое имя, альтернативный текст, описание) правятся
 * здесь же и сохраняются отдельным запросом — это поля самого вложения
 * (attachments.original_name/alt/description), а не записи, поэтому они
 * применяются сразу, не дожидаясь сохранения формы.
 */
export function MediaField({
  media,
  onChange,
  disabled,
  onUpload,
  onUpdateParams,
}: {
  media: AdMedia | null;
  onChange: (media: AdMedia | null) => void;
  disabled: boolean;
  onUpload: (file: File) => Promise<AdMedia>;
  onUpdateParams: (mediaId: number, payload: UpdateMediaPayload) => Promise<AdMedia>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingParams, setEditingParams] = useState(false);

  const onSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    // accept — лишь подсказка диалогу, реальный тип/размер проверяем сами.
    if (!/^(image|video)\//.test(file.type)) {
      setError('Можно вставить только изображение или видео.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Файл больше 2 МБ.');
      return;
    }

    setError(null);
    setUploading(true);
    try {
      onChange(await onUpload(file));
    } catch (err) {
      const message = isAxiosError<{ message?: string }>(err) ? err.response?.data?.message : null;
      setError(message || 'Не удалось загрузить файл.');
    } finally {
      setUploading(false);
    }
  };

  const isVideo = media?.mime?.startsWith('video/');

  return (
    <div className="ad-media">
      {media ? (
        <div className="ad-media__preview">
          {isVideo ? (
            <video src={media.url ?? undefined} controls />
          ) : (
            <img src={media.url ?? undefined} alt={media.alt ?? media.name ?? 'Медиафайл'} />
          )}
          <div className="ad-media__info">
            <span className="ad-media__name">{media.name}</span>
            <div className="ad-media__actions">
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                disabled={disabled || uploading}
                onClick={() => setEditingParams((v) => !v)}
              >
                {editingParams ? 'Свернуть' : 'Параметры'}
              </button>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                disabled={disabled || uploading}
                onClick={() => onChange(null)}
              >
                Убрать
              </button>
            </div>
          </div>

          {editingParams && (
            <MediaParamsForm
              media={media}
              disabled={disabled}
              onSave={async (payload) => {
                onChange(await onUpdateParams(media.id, payload));
                setEditingParams(false);
              }}
            />
          )}
        </div>
      ) : (
        <button
          type="button"
          className="ad-media__drop"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
        >
          <Icon name="plus" size={16} />
          {uploading ? 'Загрузка…' : 'Выбрать изображение или видео'}
        </button>
      )}

      <p className="edit-field__hint">Один файл, не больше 2 МБ. Форматы: изображение или видео.</p>
      {error && <p className="edit-field__hint edit-field__hint--error">{error}</p>}

      <input ref={inputRef} type="file" accept={ACCEPT} hidden onChange={onSelected} />
    </div>
  );
}

/** Поля 1:1 те, что редактировал Upload-виджет в Orchid: original_name, alt, description. */
function MediaParamsForm({
  media,
  disabled,
  onSave,
}: {
  media: AdMedia;
  disabled: boolean;
  onSave: (payload: UpdateMediaPayload) => Promise<void>;
}) {
  const [name, setName] = useState(media.name ?? '');
  const [alt, setAlt] = useState(media.alt ?? '');
  const [description, setDescription] = useState(media.description ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!name.trim()) {
      setError('Укажите отображаемое имя файла.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await onSave({ name: name.trim(), alt: alt.trim() || null, description: description.trim() || null });
    } catch (err) {
      const message = isAxiosError<{ message?: string }>(err) ? err.response?.data?.message : null;
      setError(message || 'Не удалось сохранить параметры файла.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ad-media__params">
      {error && <p className="edit-field__hint edit-field__hint--error">{error}</p>}

      <label className="edit-field">
        <span>Отображаемое имя *</span>
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} disabled={disabled || saving} />
      </label>

      <label className="edit-field">
        <span>Альтернативный текст</span>
        <input type="text" value={alt} onChange={(e) => setAlt(e.target.value)} disabled={disabled || saving} />
      </label>

      <label className="edit-field">
        <span>Описание</span>
        <textarea
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={disabled || saving}
        />
      </label>

      <button type="button" className="btn btn--primary btn--sm" onClick={save} disabled={disabled || saving}>
        Сохранить параметры
      </button>
    </div>
  );
}
